import { useQuery } from "@tanstack/react-query";
import { api } from "../utils/api";
import {
  FilterAutocompleteOption,
  getFilterAutocompleteConfig,
} from "../utils/filterAutocompleteConfig";

/**
 * Hook for fetching autocomplete options with server-side search
 * Only fetches when user types at least 1 character
 */
export const useFilterAutocompleteSearch = (
  filterName: string,
  searchTerm: string,
) => {
  const config = getFilterAutocompleteConfig(filterName);
  const shouldFetch = searchTerm.length >= 1;

  return useQuery<FilterAutocompleteOption[]>({
    queryKey: ["filterAutocompleteSearch", filterName, searchTerm],
    queryFn: async () => {
      if (!config?.endpoint || !shouldFetch) {
        return [];
      }

      try {
        const endpoint = config.endpoint.split("?")[0];
        const isOData = endpoint.includes("/Odata/");
        const hasEnableQuery = endpoint.includes("/api/v1/");

        let filter = "";
        let params: Record<string, any> = {
          $top: 100,
        };

        if (isOData) {
          const searchLower = searchTerm.toLowerCase().replace(/'/g, "''");

          if (filterName === "productCode") {
            filter = `contains(tolower(cast(productCode, 'Edm.String')), '${searchLower}')`;
          } else if (filterName === "productName") {
            filter = `contains(tolower(productName), '${searchLower}')`;
          } else if (filterName === "registrationCode") {
            filter = `contains(tolower(cast(code, 'Edm.String')), '${searchLower}') or contains(tolower(name), '${searchLower}')`;
          } else if (filterName === "genusName") {
            filter = `contains(tolower(name), '${searchLower}') or contains(tolower(cast(code, 'Edm.String')), '${searchLower}')`;
          }

          if (filter) {
            params.$filter = filter;
          }
        } else if (hasEnableQuery) {
          const searchLower = searchTerm.toLowerCase().replace(/'/g, "''");

          if (filterName === "productGroupCode") {
            filter = `contains(tolower(cast(code, 'Edm.String')), '${searchLower}') or contains(tolower(name), '${searchLower}')`;
          } else if (filterName === "gewascode") {
            filter = `contains(tolower(cast(code, 'Edm.String')), '${searchLower}') or contains(tolower(name), '${searchLower}')`;
          } else if (filterName === "gewasnummer") {
            filter = `contains(tolower(cast(taxonomicNumber, 'Edm.String')), '${searchLower}')`;
          }

          if (filter) {
            params.$filter = filter;
          }
        }

        const response = await api.get(endpoint, { params });

        let data = response.data;

        if (data?.value && Array.isArray(data.value)) {
          data = data.value;
        } else if (!Array.isArray(data)) {
          console.warn(`Unexpected data format:`, data);
          return [];
        }

        if (config.transformResponse) {
          return config.transformResponse(data);
        }

        return data as FilterAutocompleteOption[];
      } catch (error) {
        console.error(
          `Failed to load filter autocomplete options from ${config.endpoint}:`,
          error,
        );
        return [];
      }
    },
    enabled: !!config?.endpoint && shouldFetch,
    staleTime: 0,
    refetchOnWindowFocus: true,
    refetchOnMount: true,
    retry: (failureCount, error: any) => {
      if (error?.response?.status === 404) return false;
      return failureCount < 2;
    },
  });
};
